import { createContext } from 'react';

import type { AccessBlock } from '@shared/entitlementAccess';
import type { RoleId } from '@shared/roles';
import type { TeamPolicy } from '@shared/teamPolicy';
import type { DemoViewState } from '@/services/userApi';

export interface EnhancedUser {
  sub: string;
  email?: string;
  role: RoleId;
  userStatus: string;
  displayName?: string | null;
  teamPolicy: TeamPolicy;
  featureFlags: Map<string, boolean>;
  /**
   * Onboarding gate: server-computed "must sit the placement diagnostic"
   * flag from the role payload. EnhancedProtectedRoute redirects gated
   * students to /diagnostic while this is true; refreshed via
   * `refetchRole()` after the diagnostic completes.
   */
  owesDiagnostic?: boolean;
  /**
   * Access gate: server-computed block from the entitlement access policy on
   * the role payload (e.g. an expired trial). EnhancedProtectedRoute redirects
   * to `accessBlock.redirectTo` while this is set; refreshed via `refetchRole()`
   * after reactivation. Null/absent = full access.
   */
  accessBlock?: AccessBlock | null;
  [key: string]: unknown;
}

export interface AuthContextValue {
  user: EnhancedUser | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  userRole: RoleId;
  userStatus: string;
  teamPolicy: TeamPolicy;
  isRoleLoaded: boolean;
  authError: string | null;
  isServerDown: boolean;
  /**
   * Sample-student view state from the role payload, or null when the server
   * said nothing usable (older server, preview, logged out, server down).
   *
   * While `active`, `user` describes the sample student — `user.sub` alone is
   * still the signed-in teacher's Auth0 subject, because the swap is resolved
   * server-side per request, not by rewriting the token on the client.
   */
  demoView: DemoViewState | null;
  updateUserRole: (newRole: RoleId) => void;
  loginWithPath: (returnTo?: string) => void;
  logoutUser: () => void;
  getRedirectPath: () => string;
  getAccessToken: () => Promise<string>;
  refetchRole: () => Promise<void>;
  setUserStatus: React.Dispatch<React.SetStateAction<string>>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
